import { useCallback, useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Maximize2, RefreshCcw, AlertTriangle } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const MONITOR_ROLES = ["admin", "welcome_coordinator", "viewer"];
const STATE_LABELS = {
  draft: "Borrador", scheduled: "Programado", starting_soon: "Próximo", in_progress: "En curso",
  completed: "Finalizado", closed: "Cerrado", cancelled: "Cancelado",
};
const STATE_CLS = {
  scheduled: "bg-hz-blue/15 text-hz-blue", starting_soon: "bg-hz-warning/15 text-hz-warning",
  in_progress: "bg-hz-cyan/15 text-hz-cyan", completed: "bg-hz-success/15 text-hz-success",
  closed: "bg-hz-gray/30 text-hz-ink", cancelled: "bg-hz-ink/10 text-hz-ink/60", draft: "bg-hz-gray/20 text-hz-ink/50",
};
const CP_LABELS = { general_event: "Evento general", dinner: "Cena", workshop: "Taller" };

const MonitorPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hz_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  const load = useCallback(() => {
    api.get("/monitor")
      .then((r) => { setData(r.data); setUpdatedAt(new Date()); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || !MONITOR_ROLES.includes(user.role)) return;
    load();
    const iv = setInterval(load, 30000); // actualización automática cada 30 s
    return () => clearInterval(iv);
  }, [user, load]);

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-hz-navy text-white"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!user) return <Navigate to="/login?redirect=/monitor" replace />;
  if (!MONITOR_ROLES.includes(user.role)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hz-navy px-4 text-center">
        <p className="text-lg font-bold text-white" data-testid="monitor-denied">Tu rol no tiene acceso al monitor operativo.</p>
      </div>
    );
  }
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-hz-navy text-white"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const t = data.totals;
  const tiles = [
    { label: "Registrados", value: t.registered, testId: "mon-registered" },
    { label: "Evento general", value: `${t.general_checked}/${t.general_authorized}`, testId: "mon-general" },
    { label: "Asistencia general", value: `${t.general_pct}%`, testId: "mon-pct" },
    { label: "Cena", value: `${t.dinner_checked}/${t.dinner_authorized}`, testId: "mon-dinner" },
    { label: "Cena — cupo", value: t.dinner_capacity ? `${t.dinner_confirmed + t.dinner_reserved}/${t.dinner_capacity}` : "Sin límite", testId: "mon-dinner-capacity" },
    { label: "Cena — reservas pendientes", value: t.dinner_reserved ?? 0, testId: "mon-dinner-reserved" },
    { label: "Cena — lista de espera", value: t.dinner_waitlisted ?? 0, testId: "mon-dinner-waitlisted" },
    { label: "Cena — disponibles", value: t.dinner_available ?? "—", testId: "mon-dinner-available" },
  ];

  return (
    <div className="min-h-screen bg-hz-navy p-6 text-white" data-testid="monitor-page">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo variant="icon" className="h-10" />
            <div>
              <p className="text-lg font-extrabold tracking-wide">HORIZONTE 2026</p>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/60">Monitor operativo</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs text-white/60" data-testid="monitor-updated">Actualizado: {updatedAt?.toLocaleTimeString("es-MX")}</p>
            <Button size="sm" variant="outline" onClick={load} data-testid="monitor-refresh-btn" className="border-white/30 font-bold text-white hover:bg-white/10">
              <RefreshCcw className="mr-1 h-4 w-4" /> Actualizar
            </Button>
            <Button size="sm" variant="outline" onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen())} data-testid="monitor-fullscreen-btn" className="border-white/30 font-bold text-white hover:bg-white/10">
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {tiles.map((x) => (
            <div key={x.label} className="rounded-2xl bg-white/10 p-5 text-center" data-testid={x.testId}>
              <p className="text-4xl font-extrabold tabular-nums text-hz-turquoise">{x.value}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-white/70">{x.label}</p>
            </div>
          ))}
        </div>

        {data.alerts.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="monitor-alerts">
            {data.alerts.map((a, i) => (
              <p key={i} className="flex items-center gap-2 rounded-xl border border-hz-warning/40 bg-hz-warning/10 px-4 py-2 text-sm font-semibold text-hz-warning">
                <AlertTriangle className="h-4 w-4" /> {a.text}
              </p>
            ))}
          </div>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl bg-white/5 p-5">
            <p className="text-sm font-bold uppercase tracking-widest text-hz-turquoise">Talleres</p>
            <div className="mt-3 space-y-2" data-testid="monitor-workshops">
              {data.workshops.length === 0 && <p className="text-sm text-white/50">Sin talleres publicados.</p>}
              {data.workshops.map((w) => (
                <div key={w.id} className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3">
                  <div>
                    <p className="text-sm font-bold">{w.name}</p>
                    <p className="text-xs text-white/60">{w.room_name || "Salón —"} · {w.present}/{w.registered} presentes</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATE_CLS[w.state]}`}>{STATE_LABELS[w.state]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-white/5 p-5">
            <p className="text-sm font-bold uppercase tracking-widest text-hz-turquoise">Últimos accesos</p>
            <div className="mt-3 space-y-2" data-testid="monitor-recent">
              {data.recent_checkins.length === 0 && <p className="text-sm text-white/50">Aún no hay registros de acceso.</p>}
              {data.recent_checkins.map((c, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-2.5">
                  <div>
                    <p className="text-sm font-bold">{c.name}</p>
                    <p className="text-xs text-white/60">{c.folio} · {CP_LABELS[c.checkpoint_type]}{c.workshop_name ? ` · ${c.workshop_name}` : ""}</p>
                  </div>
                  <p className="text-xs text-white/50">{new Date(c.created_at).toLocaleTimeString("es-MX")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="mt-6 text-center text-[10px] font-medium uppercase tracking-widest text-white/40">
          Vista de solo lectura · No muestra datos personales ni información de pago
        </p>
      </div>
    </div>
  );
};

export default MonitorPage;
